import { useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import {
  ArrowLeft,
  Eye,
  EyeOff,
  KeyRound,
} from "lucide-react";
import {
  resetPortalPassword,
  resetStaffPassword,
  requestPortalPasswordForgot,
  requestStaffPasswordForgot,
  validateSignupPassword,
} from "@/lib/authApi";

/** Shared forgot-password UI for portal `/forgot-password` and staff `/staff-forgot-password`. */
const ForgotPassword = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const staff = pathname.includes("staff-forgot-password");

  const [step, setStep] = useState<"email" | "reset">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [codeReference, setCodeReference] = useState("");
  const [pw, setPw] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const backTarget = staff ? "/jlx-vault-entry" : "/login";

  const sendForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const api = staff
      ? () => requestStaffPasswordForgot(email)
      : () => requestPortalPasswordForgot(email);
    const { codeReference: ref, error } = await api();
    setLoading(false);
    if (error || !ref) {
      toastApiError(error?.message ?? "Missing reset session from server.");
      return;
    }
    setCodeReference(ref);
    toast.success(
      "If this email is registered, check your inbox for an OTP to finish resetting your password."
    );
    setStep("reset");
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeReference.trim()) {
      toastApiError(
        "Reset session expired. Go back and send the reset email again."
      );
      return;
    }
    if (pw !== pwConfirm) {
      toastApiError("Passwords do not match.");
      return;
    }
    const err = validateSignupPassword(pw);
    if (err) {
      toastApiError(err);
      return;
    }

    setLoading(true);
    const reset = staff ? resetStaffPassword : resetPortalPassword;
    const { error } = await reset({
      email: email.trim(),
      otp,
      code_reference: codeReference.trim(),
      password: pw,
      password_confirm: pwConfirm,
    });
    setLoading(false);
    if (error) {
      toastApiError(error.message);
      return;
    }
    toast.success("Password updated. Sign in.");
    navigate(backTarget);
  };

  return (
    <div className="bg-background flex min-h-screen flex-col justify-center px-4 py-10">
      <div className="mx-auto w-full max-w-md">
        <Link
          to={backTarget}
          className="text-muted-foreground hover:text-foreground mb-6 inline-flex items-center text-sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to login
        </Link>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <KeyRound className="text-primary h-6 w-6" />
            </div>
            <CardTitle className="font-serif text-xl">
              {staff ? "Staff reset password" : "Reset password"}
            </CardTitle>
            <CardDescription>
              {step === "email"
                ? "Enter your email. If the account exists for this tenant, we send an OTP."
                : "Enter the OTP from your email, then choose a new password."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === "email" ? (
              <form onSubmit={sendForgot} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fp-email">Email</Label>
                  <Input
                    id="fp-email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Sending…" : "Send reset"}
                </Button>
              </form>
            ) : (
              <form onSubmit={submitReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fp-email2">Email</Label>
                  <Input id="fp-email2" type="email" value={email} disabled />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fp-otp">OTP / verification code</Label>
                  <Input
                    id="fp-otp"
                    value={otp}
                    onChange={e =>
                      setOtp(e.target.value.replace(/\s/g, "").trim())
                    }
                    required
                    autoComplete="one-time-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fp-pw">New password</Label>
                  <div className="relative">
                    <Input
                      id="fp-pw"
                      type={showPw ? "text" : "password"}
                      value={pw}
                      onChange={e => setPw(e.target.value)}
                      required
                      className="pr-11"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(v => !v)}
                      className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2"
                      aria-label="Toggle password visibility"
                    >
                      {showPw ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fp-pw2">Confirm new password</Label>
                  <div className="relative">
                    <Input
                      id="fp-pw2"
                      type={showPw2 ? "text" : "password"}
                      value={pwConfirm}
                      onChange={e => setPwConfirm(e.target.value)}
                      required
                      className="pr-11"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw2(v => !v)}
                      className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2"
                      aria-label="Toggle password visibility"
                    >
                      {showPw2 ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Updating…" : "Set new password"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setStep("email");
                    setCodeReference("");
                    setOtp("");
                    setPw("");
                    setPwConfirm("");
                  }}
                >
                  Different email
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
